import { BACKEND_URL } from '@/lib/config';
import { getAuthHeaders } from '@/lib/agentApi';
import { getSelectedService } from '@/hooks/useAnalysisService';
import { withZemaxFetchSlot } from '@/lib/zemaxFetchLimiter';

/**
 * All analysis-service calls route through the Fastify backend at
 * `/api/analysis/*` rather than hitting the Python service directly. Benefits:
 *  - The Python service is no longer exposed to browsers.
 *  - On timeout / unreachable upstream, the backend synthesizes a normalized
 *    `{ success: false, errors: [{ message }] }` body so viewers render a
 *    human-readable error ("Analysis service timed out …") instead of the
 *    browser's raw "NetworkError when attempting to fetch resource".
 */

export interface AnalysisError {
  message: string;
}

interface FastApiValidationIssue {
  loc?: unknown;
  msg?: unknown;
}

/**
 * Discriminated union: on `success: true` the caller-declared payload is
 * guaranteed present; on `success: false` only `errors` is — plus
 * `user_canceled` when the service reported the run was stopped by a cancel
 * signal (tolerancing runs), which the caller shows as a stop, not a failure.
 * Endpoint-specific `warnings` and other shared fields belong in TPayload.
 */
export type AnalysisResponse<TPayload> =
  | ({ success: true } & TPayload)
  | { success: false; errors: AnalysisError[]; user_canceled?: boolean };

/**
 * The human-readable reason a proxy call failed, joined from its error list.
 * Hooks that fan a request out over several items render one of these per
 * failed item beside the results that did land.
 */
export const analysisErrorText = (r: Extract<AnalysisResponse<never>, { success: false }>): string =>
  r.errors.map((e) => e.message ?? '').filter(Boolean).join('; ') || 'request failed';

export interface PostToAnalysisOpts {
  /** Forwarded as `X-Session-Id` for log correlation in the analysis service. */
  conversationId?: string;
  /** AbortSignal for caller-driven cancellation (debounce, unmount). */
  signal?: AbortSignal;
}

export async function postToAnalysis<TPayload>(
  path: string,
  body: object,
  opts: PostToAnalysisOpts = {},
): Promise<AnalysisResponse<TPayload>> {
  return postToProxy('/api/analysis', path, body, opts);
}

/** Like {@link postToAnalysis} but routes to the Zemax orchestrator
 * (`/api/zemax/*`) instead of the Optiland analysis service. Use for
 * Zemax-specific endpoints (TDE rows, tolerancing runs, etc.) that aren't
 * served by the Optiland Python service. */
export async function postToZemax<TPayload>(
  path: string,
  body: object,
  opts: PostToAnalysisOpts = {},
): Promise<AnalysisResponse<TPayload>> {
  return postToProxy('/api/zemax', path, body, opts);
}

async function postToProxy<TPayload>(
  basePath: string,
  path: string,
  body: object,
  opts: PostToAnalysisOpts,
): Promise<AnalysisResponse<TPayload>> {
  const headers = await getAuthHeaders();
  if (opts.conversationId) headers['X-Session-Id'] = opts.conversationId;
  // Tells the /api/zemax/* proxy whether to dispatch through the user's bridge
  // tab (BYO) or forward to the hosted upstream. Optiland calls also send it
  // for consistency; the analysis proxy ignores it.
  headers['X-Analysis-Engine'] = getSelectedService();

  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const doFetch = () => fetch(`${BACKEND_URL}${basePath}/${cleanPath}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  // Zemax calls hit the serial single-file worker: funnel them through the
  // shared in-flight cap like every other worker POST path, so a tolerancing
  // run or LDE sync can't burst past the limit alongside viewer refreshes
  // and draw a 429 BRIDGE_OVERLOADED.
  const response = basePath === '/api/zemax'
    ? await withZemaxFetchSlot(doFetch, opts.signal)
    : await doFetch();

  // Both proxies normally return `{ success, errors }` JSON — even on 5xx.
  // Auth hooks and reverse proxies can return different JSON or plain text.
  // Read once as text so non-JSON bodies are still available for diagnostics.
  const bodyText = await response.text().catch(() => '');
  const normalizedResponse = normalizeAnalysisResponseBody<TPayload>(bodyText);
  if (normalizedResponse) return normalizedResponse;

  const plainTextError = normalizePlainTextError(bodyText, response.headers.get('Content-Type'));
  if (plainTextError) {
    return {
      success: false,
      errors: [{ message: `Backend returned ${response.status}: ${plainTextError}` }],
    };
  }

  return {
    success: false,
    errors: [{ message: `Backend returned ${response.status}` }],
  };
}

/** Normalize recognized JSON envelopes; callers retain diagnostics for unknown bodies. */
export function normalizeAnalysisResponseBody<TPayload>(bodyText: string): AnalysisResponse<TPayload> | null {
  const data = parseJsonBody(bodyText);

  // The authoritative `{ success }` shape wins: check it before the FastAPI
  // validation-error parser. A legit `{ success: true, detail: "..." }` payload
  // (where `detail` is just a normal field) must not be misread as a 422.
  const normalizedResponse = normalizeAnalysisResponse<TPayload>(data);
  if (normalizedResponse) return normalizedResponse;

  // FastAPI 422 bodies are `{ detail: [...] }` with NO `success` field. Only
  // parse them when `success` is absent — an explicit `success` always wins.
  const hasSuccessField = !!data && typeof data === 'object' && 'success' in data;
  if (!hasSuccessField) {
    const validationErrors = normalizeFastApiValidationErrors(data);
    if (validationErrors) {
      return { success: false, errors: validationErrors };
    }
  }

  const backendError = normalizeBackendError(data);
  if (backendError) {
    return { success: false, errors: [{ message: backendError }] };
  }

  return null;
}

function parseJsonBody(bodyText: string): unknown {
  if (bodyText.trim() === '') return null;
  try {
    return JSON.parse(bodyText) as unknown;
  } catch {
    return null;
  }
}

function normalizeBackendError(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;

  const record = data as { error?: unknown; message?: unknown };
  // Fastify and the database error handler use error for a category/code and
  // message for the explanation intended for the user.
  if (typeof record.message === 'string' && record.message.trim() !== '') {
    return record.message;
  }
  if (typeof record.error === 'string' && record.error.trim() !== '') {
    return record.error;
  }
  return null;
}

function normalizeAnalysisResponse<TPayload>(data: unknown): AnalysisResponse<TPayload> | null {
  if (!data || typeof data !== 'object' || !('success' in data)) return null;

  const record = data as { success: unknown; errors?: unknown; user_canceled?: unknown };
  if (record.success === true) return data as { success: true } & TPayload;
  if (record.success !== false) return null;

  if (Array.isArray(record.errors)) {
    const errors = record.errors
      .map((error) => {
        if (error && typeof error === 'object' && typeof (error as AnalysisError).message === 'string') {
          return { message: (error as AnalysisError).message };
        }
        if (typeof error === 'string' && error.trim() !== '') {
          return { message: error };
        }
        return null;
      })
      .filter((error): error is AnalysisError => error !== null);
    return { success: false, errors, ...(record.user_canceled === true ? { user_canceled: true } : {}) };
  }

  return null;
}

function normalizePlainTextError(bodyText: string, contentType: string | null): string | null {
  const normalizedContentType = contentType?.toLowerCase() ?? '';
  if (normalizedContentType.includes('text/html') || normalizedContentType.includes('json')) return null;

  let message = bodyText.trim();
  if (!message) return null;

  message = message.replace(/\s+/g, ' ').trim();
  return message || null;
}

function normalizeFastApiValidationErrors(data: unknown): AnalysisError[] | null {
  if (!data || typeof data !== 'object' || !('detail' in data)) return null;

  const detail = (data as { detail: unknown }).detail;
  if (typeof detail === 'string') return [{ message: detail }];
  if (!Array.isArray(detail)) return null;

  const errors = detail
    .map((issue: FastApiValidationIssue) => {
      if (!issue || typeof issue !== 'object' || typeof issue.msg !== 'string') {
        return null;
      }

      const path = Array.isArray(issue.loc)
        ? issue.loc
          .slice(1)
          .map(String)
          .join('.')
        : '';
      return { message: path ? `${path}: ${issue.msg}` : issue.msg };
    })
    .filter((error): error is AnalysisError => error !== null);

  return errors.length > 0 ? errors : null;
}
